import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Stack,
  InputAdornment,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import {
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  BugReport as BugReportIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  DateRange as DateRangeIcon,
} from "@mui/icons-material";
import axios from "axios";
import { Autocomplete } from "@mui/material";
import { useSelector } from "react-redux";

const DEV_USER = [
  "dmeszaros+butcher@sr360solutions.com",
  "dmeszaros+ronald@sr360solutions.com",
];

// Available pages for modification requests
const AVAILABLE_PAGES = [
  // Main User Pages
  { path: "/dashboard", name: "Dashboard" },
  { path: "/permits/create", name: "Create NOC" },
  { path: "/building-permit", name: "Building Permit" },
  { path: "/selectcounties", name: "Select Counties" },
  { path: "/defaults", name: "Presets" },
  { path: "/permits/my-permits", name: "My Permits" },
  { path: "/permits/invitations", name: "Permit Invitations" },
  { path: "/custom-documents/sign", name: "Custom Documents Sign" },
  { path: "/custom-documents/noc", name: "Custom Documents NOC" },
  {
    path: "/custom-documents/prepared-docs",
    name: "Custom Documents Prepared",
  },
  {
    path: "/privateProvider/contractors",
    name: "Private Provider Contractors",
  },
  { path: "/profilesettings", name: "Profile Settings" },
  { path: "/contractors", name: "Contractors" },
  { path: "/payment", name: "Payment" },
  { path: "/subusers", name: "Subusers" },
  { path: "/subscription", name: "Subscription" },

  // Admin Pages
  { path: "/admin/notaries", name: "Admin Notaries" },
  { path: "/admin/overview", name: "Admin Overview" },
  { path: "/admin/customers", name: "Admin Customers" },
  { path: "/admin/tasks", name: "Admin Tasks" },
  { path: "/admin/licenses", name: "Admin Licenses" },
  { path: "/admin/management", name: "Admin Management" },
  { path: "/admin/reports", name: "Admin Reports" },

  // Customer Management & License Operations
  {
    path: "customer_management/license_number_editing",
    name: "License Number Editing for Existing Customers",
  },
  {
    path: "customer_management/license_numbers",
    name: "License Numbers",
  },

  // Notary Pages
  { path: "/notary/dashboard", name: "Notary Dashboard" },
  { path: "/notary/organization", name: "Notary Organization" },
  { path: "/notary/settings", name: "Notary Settings" },
  { path: "/notary/finalize", name: "Notary Finalize" },
  { path: "/notary/queue", name: "Notary Queue" },
  { path: "/notary/history", name: "Notary History" },
  { path: "/notary/contact", name: "Notary Contact" },

  // Authentication & Security Pages
  { path: "/login", name: "Login" },
  { path: "/register", name: "Register" },
  { path: "/resetpassword", name: "Reset Password" },
  { path: "/2fa-secure", name: "Two-Factor Authentication" },
  { path: "/2fa-secure/email", name: "Email 2FA" },
  { path: "/2fa-secure/totp", name: "TOTP 2FA" },
  { path: "/2fa-secure/sms", name: "SMS 2FA" },
  { path: "/2fa-secure/email/pass", name: "Email 2FA Password" },
  { path: "/2fa-secure/reset-pwd/totp", name: "Reset Password TOTP" },
  { path: "/2fa-secure/reset-pwd/sms", name: "Reset Password SMS" },
  { path: "/2fa-secure/reset-pwd/email", name: "Reset Password Email" },
  { path: "/2fa-secure/email/reset/pass", name: "Reset Password Email Pass" },

  // Guest & Public Pages
  { path: "/", name: "Home/Landing Page" },
  { path: "/notFound", name: "404 Not Found Page" },
  { path: "/notary/register", name: "Notary Registration" },
  { path: "/notary/accept-instant", name: "Instant Notary Acceptance" },
  { path: "/signee/auth-login", name: "Signee Authentication" },
  { path: "/notary/signee/register", name: "Notary Signee Registration" },
  { path: "/record/signee/register", name: "Record Signee Registration" },
  { path: "/signee/manage", name: "Signee Management" },
  { path: "/notary/contact", name: "Notary Contact" },

  // RON (Remote Online Notarization) Pages
  { path: "/ron/user/completed", name: "User RON Completed" },
  { path: "/ron/notary/completed", name: "Notary RON Completed" },

  // Success & Status Pages
  { path: "/changed-plan", name: "Plan Changed Success" },
  { path: "/created-expired-plan", name: "Expired Plan Created" },
  { path: "/subuser/finalize", name: "Subuser Finalization" },

  // Document Types & Workflows
  {
    path: "document_types/noc",
    name: "Notice of Commencement (NOC) Documents",
  },
  { path: "document_types/building_permit", name: "Building Permit Documents" },
  { path: "document_types/energy_forms", name: "Energy Forms & Documents" },
  { path: "document_types/roofing", name: "Roofing Documents" },
  { path: "document_types/custom_docs", name: "Custom Document Templates" },
  { path: "document_types/notary_forms", name: "Notary Forms & Affidavits" },
  {
    path: "document_types/contractor_agreements",
    name: "Contractor Agreements",
  },
  { path: "document_types/owner_agreements", name: "Owner Agreements" },

  // Permit Forms & Applications
  { path: "permit_forms/building_permit_forms", name: "Building Permit Forms" },
  {
    path: "permit_forms/electrical_permit_forms",
    name: "Electrical Permit Forms",
  },
  {
    path: "permit_forms/mechanical_permit_forms",
    name: "Mechanical Permit Forms",
  },
  { path: "permit_forms/plumbing_permit_forms", name: "Plumbing Permit Forms" },
  { path: "permit_forms/roofing_permit_forms", name: "Roofing Permit Forms" },
  { path: "permit_forms/sign_permit_forms", name: "Sign Permit Forms" },
  { path: "permit_forms/fire_permit_forms", name: "Fire Permit Forms" },
  {
    path: "permit_forms/mobile_home_permit_forms",
    name: "Mobile Home Permit Forms",
  },
  {
    path: "permit_forms/right_of_way_permit_forms",
    name: "Right of Way Permit Forms",
  },
  { path: "permit_forms/sitework_permit_forms", name: "Sitework Permit Forms" },
  {
    path: "permit_forms/demolition_permit_forms",
    name: "Demolition Permit Forms",
  },
  { path: "permit_forms/asbestos_permit_forms", name: "Asbestos Permit Forms" },
  { path: "permit_forms/drainage_permit_forms", name: "Drainage Permit Forms" },
  {
    path: "permit_forms/encroachment_permit_forms",
    name: "Encroachment Permit Forms",
  },
  {
    path: "permit_forms/stormwater_permit_forms",
    name: "Stormwater Permit Forms",
  },
  {
    path: "permit_forms/swimming_pool_permit_forms",
    name: "Swimming Pool Permit Forms",
  },
  { path: "permit_forms/window_permit_forms", name: "Window Permit Forms" },
  { path: "permit_forms/sunroom_permit_forms", name: "Sunroom Permit Forms" },
  {
    path: "permit_forms/ownership_permit_forms",
    name: "Ownership Permit Forms",
  },
  { path: "permit_forms/obtain_permit_forms", name: "Obtain Permit Forms" },
  { path: "permit_forms/pais_permit_forms", name: "PAIS Permit Forms" },
  { path: "permit_forms/pass_permit_forms", name: "PASS Permit Forms" },
  {
    path: "permit_forms/septic_permit_forms",
    name: "Septic System Permit Forms",
  },
  {
    path: "permit_forms/private_provider_forms",
    name: "Private Provider Forms",
  },
  { path: "permit_forms/complex_permit_forms", name: "Complex Permit Forms" },
  { path: "permit_forms/simple_permit_forms", name: "Simple Permit Forms" },

  // Coupons & Discounts
  { path: "coupons/coupon_management", name: "Coupon Management System" },
  {
    path: "coupons/discount_creation",
    name: "Discount Creation & Configuration",
  },
  { path: "coupons/coupon_assignment", name: "Coupon Assignment to Users" },
  { path: "coupons/coupon_removal", name: "Coupon Removal from Users" },
  { path: "coupons/discount_categories", name: "Discount Categories & Types" },
  { path: "coupons/notary_record_discounts", name: "Notary Record Discounts" },
  { path: "coupons/noc_notary_discounts", name: "NOC Notary Discounts" },
  {
    path: "coupons/extra_county_discounts",
    name: "Extra County Cost Discounts",
  },
  {
    path: "coupons/extra_download_discounts",
    name: "Extra Download Cost Discounts",
  },
  { path: "coupons/monthly_price_discounts", name: "Monthly Price Discounts" },
  { path: "coupons/signup_discounts", name: "Signup Discount Management" },
  {
    path: "coupons/stripe_coupon_integration",
    name: "Stripe Coupon Integration",
  },
  {
    path: "coupons/coupon_validation",
    name: "Coupon Validation & Verification",
  },
  {
    path: "coupons/discount_expiration",
    name: "Discount Expiration Management",
  },
  { path: "coupons/coupon_usage_tracking", name: "Coupon Usage Tracking" },
  {
    path: "coupons/discount_analytics",
    name: "Discount Analytics & Reporting",
  },

  // City Site Connections & Integration
  {
    path: "city_sites/jaxepics_integration",
    name: "JAXEPICS Integration System",
  },
  {
    path: "city_sites/city_credentials_management",
    name: "City Credentials Management",
  },
  {
    path: "city_sites/city_site_authentication",
    name: "City Site Authentication",
  },
  {
    path: "city_sites/permit_connection_system",
    name: "Permit Connection System",
  },
  {
    path: "city_sites/city_permit_upload",
    name: "City Permit Document Upload",
  },
  { path: "city_sites/jaxepics_client", name: "JAXEPICS Client Integration" },
  { path: "city_sites/city_site_scraping", name: "City Site Data Scraping" },
  {
    path: "city_sites/permit_field_mapping",
    name: "Permit Field Mapping & Binds",
  },
  { path: "city_sites/document_binds", name: "Document Binds & Attachments" },
  { path: "city_sites/city_user_management", name: "City User Management" },
  { path: "city_sites/city_permit_panel", name: "City Permit Panel Interface" },
  { path: "city_sites/auto_upload_system", name: "Auto Upload System" },
  { path: "city_sites/city_permit_validation", name: "City Permit Validation" },
  {
    path: "city_sites/proxy_management",
    name: "Proxy Management for City Sites",
  },
  {
    path: "city_sites/city_site_error_handling",
    name: "City Site Error Handling",
  },
  {
    path: "city_sites/city_connection_status",
    name: "City Connection Status Tracking",
  },
  {
    path: "city_sites/jaxepics_api_integration",
    name: "JAXEPICS API Integration",
  },
  {
    path: "city_sites/city_permit_workflow",
    name: "City Permit Workflow Management",
  },

  // Verification & Authentication Methods
  { path: "verification/id_verification", name: "ID Verification System" },
  {
    path: "verification/notary_verification",
    name: "Notary Verification Process",
  },
  {
    path: "verification/user_verification",
    name: "User Verification Workflow",
  },
  {
    path: "verification/document_verification",
    name: "Document Verification System",
  },
  {
    path: "verification/email_verification",
    name: "Email Verification Process",
  },
  { path: "verification/sms_verification", name: "SMS Verification Process" },
  {
    path: "verification/2fa_verification",
    name: "Two-Factor Authentication Verification",
  },

  // Signing & Notarization Methods
  {
    path: "signing/ron_signing",
    name: "RON (Remote Online Notarization) Process",
  },
  { path: "signing/esign_signing", name: "eSign (Electronic Signing) Process" },
  { path: "signing/in_person_signing", name: "In-Person Notarization" },
  { path: "signing/hybrid_signing", name: "Hybrid Signing (Mixed Methods)" },
  { path: "signing/batch_signing", name: "Batch Document Signing" },
  { path: "signing/sequential_signing", name: "Sequential Document Signing" },
  { path: "signing/parallel_signing", name: "Parallel Document Signing" },

  // User Experience & Workflow
  { path: "ux/id_verification_skip", name: "ID Verification Skip Logic" },
  { path: "ux/notary_reuse_verification", name: "Notary Verification Reuse" },
  { path: "ux/user_session_management", name: "User Session Management" },
  {
    path: "ux/verification_reminders",
    name: "Verification Reminders & Notifications",
  },
  { path: "ux/verification_timeout", name: "Verification Timeout Settings" },
  { path: "ux/verification_frequency", name: "Verification Frequency Rules" },

  // Compliance & Legal
  { path: "compliance/notary_laws", name: "Notary Law Compliance" },
  {
    path: "compliance/electronic_signature_laws",
    name: "Electronic Signature Laws",
  },
  {
    path: "compliance/identity_verification_laws",
    name: "Identity Verification Laws",
  },
  { path: "compliance/record_keeping", name: "Record Keeping Requirements" },
  { path: "compliance/audit_trails", name: "Audit Trail Compliance" },

  // System Areas (Non-Page Specific)
  { path: "email_templates", name: "Email Templates" },
  { path: "notification_system", name: "Notification System" },
  { path: "error_pages", name: "Error Pages (400, 500, etc.)" },
  { path: "loading_states", name: "Loading States & Spinners" },
  { path: "form_validation", name: "Form Validation Messages" },
  { path: "navigation_menu", name: "Navigation Menu & Sidebar" },
  { path: "footer", name: "Footer & Legal Pages" },
  { path: "mobile_responsiveness", name: "Mobile Responsiveness" },
  { path: "accessibility", name: "Accessibility Features" },
  { path: "performance", name: "Performance & Loading Speed" },
  { path: "search_functionality", name: "Search & Filter Functions" },
  { path: "data_export", name: "Data Export & Reports" },
  { path: "user_permissions", name: "User Permissions & Roles" },
  { path: "audit_logs", name: "Audit Logs & Activity Tracking" },
  { path: "backup_system", name: "Backup & Recovery System" },
  { path: "api_endpoints", name: "API Endpoints & Documentation" },
  { path: "database_schema", name: "Database Schema & Structure" },
  { path: "third_party_integrations", name: "Third-Party Integrations" },
  { path: "security_features", name: "Security Features & Encryption" },
  { path: "compliance", name: "Compliance & Legal Requirements" },
];

const URGENCY_LEVELS = [
  { value: 1, label: "Low", color: "success" },
  { value: 2, label: "Medium", color: "warning" },
  { value: 3, label: "High", color: "error" },
];

const SERVER_TYPES = [
  { value: "both", label: "Both" },
  { value: "dev", label: "Development" },
  { value: "production", label: "Production" },
];

const STATUS_COLORS = {
  pending: "warning",
  accepted: "info",
  rejected: "error",
  completed: "success",
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Filter Component
function TicketFilters({
  filters,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  ticketType,
}) {
  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <FilterListIcon color="primary" />
        <Typography variant="h6">Filters</Typography>
      </Box>

      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth
            type="date"
            label="Date From"
            value={filters.dateFrom}
            onChange={(e) => onFilterChange("dateFrom", e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DateRangeIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth
            type="date"
            label="Date To"
            value={filters.dateTo}
            onChange={(e) => onFilterChange("dateTo", e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DateRangeIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 2 }}>
          <FormControl fullWidth variant="filled">
            <InputLabel>Urgency</InputLabel>
            <Select
              value={filters.urgency}
              onChange={(e) => onFilterChange("urgency", e.target.value)}
              label="Urgency"
            >
              <MenuItem value="">All</MenuItem>
              {URGENCY_LEVELS.map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  <Chip
                    label={level.label}
                    color={level.color}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  {level.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 2 }}>
          <FormControl fullWidth variant="filled">
            <InputLabel>Server Type</InputLabel>
            <Select
              value={filters.serverType}
              onChange={(e) => onFilterChange("serverType", e.target.value)}
              label="Server Type"
            >
              <MenuItem value="">All</MenuItem>
              {SERVER_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 2 }}>
          <FormControl fullWidth variant="filled">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => onFilterChange("status", e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Search"
            value={filters.search}
            onChange={(e) => onFilterChange("search", e.target.value)}
            placeholder="Search tickets..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => onApplyFilters(ticketType)}
              startIcon={<FilterListIcon />}
            >
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              onClick={() => onClearFilters(ticketType)}
            >
              Clear Filters
            </Button>
          </Stack>
        </Grid2>
      </Grid2>
    </Paper>
  );
}

const Reports = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);

  const [tabValue, setTabValue] = useState(0);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportContent, setReportContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [error, setError] = useState("");

  // Support ticket states
  const [supportTickets, setSupportTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketError, setTicketError] = useState("");

  // Completed tickets states
  const [completedTickets, setCompletedTickets] = useState([]);
  const [loadingCompletedTickets, setLoadingCompletedTickets] = useState(false);
  const [completedTicketError, setCompletedTicketError] = useState("");

  // Rejected tickets states
  const [rejectedTickets, setRejectedTickets] = useState([]);
  const [loadingRejectedTickets, setLoadingRejectedTickets] = useState(false);
  const [rejectedTicketError, setRejectedTicketError] = useState("");

  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    urgency: "",
    serverType: "",
    status: "",
    search: "",
  });

  // Pagination states
  const [pagination, setPagination] = useState({
    supportTickets: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    completedTickets: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    rejectedTickets: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  });

  const [ticketType, setTicketType] = useState("modification");
  const [selectedPage, setSelectedPage] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [urgency, setUrgency] = useState(1);
  const [serverType, setServerType] = useState("both");
  const [submittingTicket, setSubmittingTicket] = useState(false);

  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    action: null,
    ticketId: null,
  });

  // Fetch reports list
  const fetchReports = useCallback(async () => {
    if (!currentSession) return;

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/getDevReports`,
        { currentSession },
        { withCredentials: true }
      );

      if (response.data.success) {
        setReports(response.data.data);
        // Clear selection if the selected report is no longer in the list
        if (
          selectedReport &&
          !response.data.data.find(
            (r) => r.filename === selectedReport.filename
          )
        ) {
          setSelectedReport(null);
          setReportContent("");
        }
      } else {
        setError(response.data.message || "Failed to fetch reports");
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      setError(error.response?.data?.message || "Failed to connect to server");
    } finally {
      setLoading(false);
    }
  }, [currentSession, selectedReport]);

  // Fetch report content
  const fetchReportContent = async (report) => {
    if (!currentSession || !report) return;

    setLoadingContent(true);
    setError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/getDevReportContent`,
        {
          currentSession,
          filename: report.filename,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setReportContent(response.data.data.content);
      } else {
        setError(response.data.message || "Failed to fetch report content");
      }
    } catch (error) {
      console.error("Error fetching report content:", error);
      setError(error.response?.data?.message || "Failed to read report file");
    } finally {
      setLoadingContent(false);
    }
  };

  // Fetch support tickets (completed, rejected, and non-completed)
  const fetchSupportTickets = useCallback(
    async (page = 1, filters = {}) => {
      if (!currentSession) return;

      setLoadingTickets(true);
      setLoadingCompletedTickets(true);
      setLoadingRejectedTickets(true);
      setTicketError("");
      setCompletedTicketError("");
      setRejectedTicketError("");

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/admin/getSupportTickets`,
          {
            currentSession,
            page,
            limit: pagination.supportTickets.limit,
            filters,
          },
          { withCredentials: true }
        );

        if (response.data.success) {
          // Set non-completed tickets
          setSupportTickets(response.data.tickets);
          setPagination((prev) => ({
            ...prev,
            supportTickets: {
              ...prev.supportTickets,
              page: response.data.page || page,
              total: response.data.total || 0,
              totalPages: response.data.totalPages || 0,
            },
          }));

          // Set completed tickets
          setCompletedTickets(response.data.completedTickets);
          setPagination((prev) => ({
            ...prev,
            completedTickets: {
              ...prev.completedTickets,
              page: response.data.completedPage || page,
              total: response.data.completedTotal || 0,
              totalPages: response.data.completedTotalPages || 0,
            },
          }));

          // Set rejected tickets
          setRejectedTickets(response.data.rejectedTickets);
          setPagination((prev) => ({
            ...prev,
            rejectedTickets: {
              ...prev.rejectedTickets,
              page: response.data.rejectedPage || page,
              total: response.data.rejectedTotal || 0,
              totalPages: response.data.rejectedTotalPages || 0,
            },
          }));
        } else {
          setTicketError(
            response.data.message || "Failed to fetch support tickets"
          );
        }
      } catch (error) {
        console.error("Error fetching support tickets:", error);
        setTicketError(
          error.response?.data?.message || "Failed to connect to server"
        );
      } finally {
        setLoadingTickets(false);
        setLoadingCompletedTickets(false);
        setLoadingRejectedTickets(false);
      }
    },
    [currentSession, pagination.supportTickets.limit]
  );

  // Create support ticket
  const createSupportTicket = async () => {
    if (!currentSession) return;

    setSubmittingTicket(true);

    try {
      const ticketData = {
        type: ticketType,
        title,
        description,
        urgency,
        serverType,
        ...(ticketType === "modification" && { page: selectedPage }),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/createSupportTicket`,
        {
          currentSession,
          ticketData,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        resetTicketForm();
        fetchSupportTickets(pagination.supportTickets.page, filters);
      } else {
        setTicketError(
          response.data.message || "Failed to create support ticket"
        );
      }
    } catch (error) {
      console.error("Error creating support ticket:", error);
      setTicketError(
        error.response?.data?.message || "Failed to connect to server"
      );
    } finally {
      setSubmittingTicket(false);
    }
  };

  // Update ticket status
  const updateTicketStatus = async (ticketId, status) => {
    if (!currentSession) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/updateSupportTicketStatus`,
        {
          currentSession,
          ticket_id: ticketId,
          status,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        // Refresh both ticket lists since a ticket might move between completed and non-completed
        fetchSupportTickets(pagination.supportTickets.page, filters);
      } else {
        setTicketError(
          response.data.message || "Failed to update ticket status"
        );
      }
    } catch (error) {
      console.error("Error updating ticket status:", error);
      setTicketError(
        error.response?.data?.message || "Failed to connect to server"
      );
    }
  };

  // Delete ticket
  const deleteTicket = async (ticketId) => {
    if (!currentSession) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/deleteSupportTicket`,
        {
          currentSession,
          ticket_id: ticketId,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        // Refresh both ticket lists since a ticket might be deleted from either list
        fetchSupportTickets(pagination.supportTickets.page, filters);
      } else {
        setTicketError(response.data.message || "Failed to delete ticket");
      }
    } catch (error) {
      console.error("Error deleting ticket:", error);
      setTicketError(
        error.response?.data?.message || "Failed to connect to server"
      );
    }
  };

  // Reset ticket form
  const resetTicketForm = () => {
    setTicketType(tabValue === 1 ? "modification" : "feature");
    setSelectedPage("");
    setTitle("");
    setDescription("");
    setUrgency(1);
    setServerType("both");
  };

  // Confirmation dialog functions
  const openConfirmDialog = (title, message, action, ticketId) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      action,
      ticketId,
    });
  };

  // Open confirmation dialog for support ticket creation
  const openCreateTicketConfirmDialog = () => {
    const ticketTypeLabel =
      ticketType === "modification"
        ? "Modification Request"
        : "Feature Request";
    const pageInfo =
      ticketType === "modification" && selectedPage
        ? ` for ${selectedPage}`
        : "";

    openConfirmDialog(
      `Confirm ${ticketTypeLabel}`,
      `Are you sure you want to submit this ${ticketTypeLabel.toLowerCase()}${pageInfo}? This will create a new support ticket that will be reviewed by the development team.`,
      "create_ticket",
      null
    );
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      action: null,
      ticketId: null,
    });
  };

  const handleConfirmAction = async () => {
    if (confirmDialog.action === "create_ticket") {
      await createSupportTicket();
    } else if (confirmDialog.action && confirmDialog.ticketId) {
      if (confirmDialog.action === "delete") {
        await deleteTicket(confirmDialog.ticketId);
      } else {
        await updateTicketStatus(confirmDialog.ticketId, confirmDialog.action);
      }
    }
    closeConfirmDialog();
  };

  // Initial load
  useEffect(() => {
    if (currentSession) {
      fetchReports();
      fetchSupportTickets();
    }
  }, [currentSession, fetchReports, fetchSupportTickets]);

  // Load tickets when tab changes
  useEffect(() => {
    if (currentSession && tabValue >= 3) {
      // Support tickets tab and beyond
      fetchSupportTickets();
    }
  }, [currentSession, tabValue, fetchSupportTickets]);

  const handleReportSelect = (report) => {
    setSelectedReport(report);
    fetchReportContent(report);
  };

  const handleRefresh = () => {
    fetchReports();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Set ticket type based on tab
    if (newValue === 1) {
      setTicketType("modification");
    } else if (newValue === 2) {
      setTicketType("feature");
    }
  };

  // Filter handlers
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const applyFilters = (ticketType = "support") => {
    setPagination((prev) => ({
      ...prev,
      supportTickets: { ...prev.supportTickets, page: 1 },
      completedTickets: { ...prev.completedTickets, page: 1 },
      rejectedTickets: { ...prev.rejectedTickets, page: 1 },
    }));
    fetchSupportTickets(1, filters);
  };

  const clearFilters = (ticketType = "support") => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      urgency: "",
      serverType: "",
      status: "",
      search: "",
    });
    setPagination((prev) => ({
      ...prev,
      supportTickets: { ...prev.supportTickets, page: 1 },
      completedTickets: { ...prev.completedTickets, page: 1 },
      rejectedTickets: { ...prev.rejectedTickets, page: 1 },
    }));
    fetchSupportTickets(1, {});
  };

  // Pagination handlers
  const handlePageChange = (event, page, ticketType = "support") => {
    if (ticketType === "support") {
      setPagination((prev) => ({
        ...prev,
        supportTickets: { ...prev.supportTickets, page },
      }));
      fetchSupportTickets(page, filters);
    } else {
      setPagination((prev) => ({
        ...prev,
        completedTickets: { ...prev.completedTickets, page },
      }));
      fetchSupportTickets(page, filters);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatFileSize = (sizeStr) => {
    const size = parseInt(sizeStr);
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getUrgencyColor = (level) => {
    const urgency = URGENCY_LEVELS.find((u) => u.value === level);
    return urgency ? urgency.color : "default";
  };

  const getStatusColor = (status) => {
    return STATUS_COLORS[status] || "default";
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          mb: 3,

          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography color="primary" variant="h4" component="h1" gutterBottom>
            Development Progress Reports
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View daily development progress reports and updates
          </Typography>
        </Box>
        <Tooltip title="Refresh Reports">
          <IconButton
            className={loading ? "rotate" : ""}
            onClick={handleRefresh}
            disabled={loading}
            color="primary"
            size="large"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {ticketError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setTicketError("")}
        >
          {ticketError}
        </Alert>
      )}

      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Reports and Support Tabs"
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab label="Development Reports" />
          <Tab label="Request Modification" />
          <Tab label="Request New Feature" />
          <Tab label="Active Tickets" />
          <Tab label="Completed Tickets" />
          <Tab label="Rejected Tickets" />
        </Tabs>

        {/* Development Reports Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid2 container spacing={3} sx={{ height: "calc(100vh - 400px)" }}>
            {/* Left Panel - Report Files List */}
            <Grid2
              size={{ xs: 12, md: 4 }}
              sx={{
                overflowY: "auto",
                height: "100%",
                borderRight: 3,
                borderColor: "divider",
              }}
            >
              <Paper
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Typography
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <DescriptionIcon color="primary" />
                    Report Files
                    {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
                  </Typography>
                </Box>

                {loading && reports.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : reports.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                      color: "text.secondary",
                      p: 3,
                    }}
                  >
                    <DescriptionIcon
                      sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}
                    />
                    <Typography variant="h6" gutterBottom>
                      No Reports Found
                    </Typography>
                    <Typography variant="body2" textAlign="center">
                      No development progress reports are currently available
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ flex: 1, overflow: "auto" }}>
                    {reports.map((report, index) => (
                      <React.Fragment key={report.id}>
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => handleReportSelect(report)}
                            selected={selectedReport?.id === report.id}
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: "primary.light",
                                "&:hover": {
                                  backgroundColor: "primary.light",
                                },
                              },
                            }}
                          >
                            <ListItemText
                              primary={formatDate(report.date)}
                              secondary={`${report.filename} • ${formatFileSize(report.size)}`}
                              slotProps={{
                                primary: {
                                  sx: {
                                    fontWeight:
                                      selectedReport?.id === report.id
                                        ? "bold"
                                        : "normal",
                                  },
                                },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        {index < reports.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid2>

            {/* Right Panel - Report Content */}
            <Grid2
              size={{ xs: 12, md: 8 }}
              sx={{ overflowY: "auto", height: "100%" }}
            >
              <Paper
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Typography
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <AssessmentIcon color="primary" />
                    {selectedReport
                      ? `Report: ${formatDate(selectedReport.date)}`
                      : "Select a report to view"}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
                  {!selectedReport ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        color: "text.secondary",
                      }}
                    >
                      <DescriptionIcon
                        sx={{ fontSize: 64, mb: 2, opacity: 0.5 }}
                      />
                      <Typography variant="h6" gutterBottom>
                        No Report Selected
                      </Typography>
                      <Typography variant="body2" textAlign="center">
                        Choose a report from the left panel to view its content
                      </Typography>
                    </Box>
                  ) : loadingContent ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box>
                      {error ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {error}
                        </Alert>
                      ) : (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            backgroundColor: "grey.50",
                            fontFamily: "monospace",
                            whiteSpace: "pre-wrap",
                            fontSize: "14px",
                            lineHeight: 1.6,
                            maxHeight: "100%",
                            overflow: "auto",
                          }}
                        >
                          {reportContent || "No content available"}
                        </Paper>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid2>
          </Grid2>
        </TabPanel>

        {/* Request Modification Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <BugReportIcon color="primary" />
              Request Modification
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Submit a request to modify an existing page or feature
            </Typography>

            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12 }}>
                <Autocomplete
                  fullWidth
                  required
                  id="select-page"
                  options={AVAILABLE_PAGES}
                  getOptionLabel={(option) => option.name}
                  value={
                    AVAILABLE_PAGES.find(
                      (page) => page.path === selectedPage
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    setSelectedPage(newValue ? newValue.path : "")
                  }
                  groupBy={(option) => {
                    if (
                      [
                        "/dashboard",
                        "/permits/create",
                        "/building-permit",
                        "/selectcounties",
                        "/defaults",
                        "/permits/my-permits",
                        "/permits/invitations",
                        "/custom-documents/sign",
                        "/custom-documents/noc",
                        "/custom-documents/prepared-docs",
                        "/privateProvider/contractors",
                        "/profilesettings",
                        "/contractors",
                        "/payment",
                        "/subusers",
                        "/subscription",
                      ].includes(option.path)
                    ) {
                      return "Main User Pages";
                    } else if (option.path.startsWith("/admin/")) {
                      return "Admin Pages";
                    } else if (option.path.startsWith("/notary/")) {
                      return "Notary Pages";
                    } else if (
                      [
                        "/login",
                        "/register",
                        "/resetpassword",
                        "/2fa-secure",
                        "/2fa-secure/email",
                        "/2fa-secure/totp",
                        "/2fa-secure/sms",
                        "/2fa-secure/email/pass",
                        "/2fa-secure/reset-pwd/totp",
                        "/2fa-secure/reset-pwd/sms",
                        "/2fa-secure/reset-pwd/email",
                        "/2fa-secure/email/reset/pass",
                      ].includes(option.path)
                    ) {
                      return "Authentication & Security";
                    } else if (
                      [
                        "/",
                        "/notFound",
                        "/notary/register",
                        "/notary/accept-instant",
                        "/signee/auth-login",
                        "/notary/signee/register",
                        "/record/signee/register",
                        "/signee/manage",
                        "/notary/contact",
                      ].includes(option.path)
                    ) {
                      return "Guest & Public Pages";
                    } else if (option.path.startsWith("/ron/")) {
                      return "RON (Remote Online Notarization)";
                    } else if (
                      [
                        "/changed-plan",
                        "/created-expired-plan",
                        "/subuser/finalize",
                      ].includes(option.path)
                    ) {
                      return "Success & Status Pages";
                    } else if (option.path.startsWith("document_types/")) {
                      return "Document Types & Workflows";
                    } else if (option.path.startsWith("permit_forms/")) {
                      return "Permit Forms & Applications";
                    } else if (option.path.startsWith("coupons/")) {
                      return "Coupons & Discounts";
                    } else if (option.path.startsWith("city_sites/")) {
                      return "City Site Connections & Integration";
                    } else if (option.path.startsWith("verification/")) {
                      return "Verification & Authentication Methods";
                    } else if (option.path.startsWith("signing/")) {
                      return "Signing & Notarization Methods";
                    } else if (option.path.startsWith("ux/")) {
                      return "User Experience & Workflow";
                    } else if (option.path.startsWith("compliance/")) {
                      return "Compliance & Legal";
                    } else if (option.path.startsWith("customer_management/")) {
                      return "Customer Management & License Operations";
                    } else {
                      return "System Areas & Infrastructure";
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Area/Page"
                      variant="filled"
                      required
                      helperText="Choose the area or page where the modification should take place"
                    />
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <Box
                        sx={{
                          position: "sticky",
                          top: -8,
                          pt: 1,
                          pb: 1,
                          backgroundColor: "background.paper",
                          fontWeight: "bold",
                          color: "primary.main",
                          fontSize: "0.875rem",
                          px: 2,
                          py: 0.5,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        {params.group}
                      </Box>
                      <Box component="ul" sx={{ p: 0, m: 0 }}>
                        {params.children}
                      </Box>
                    </li>
                  )}
                  sx={{
                    maxWidth: "100%",
                    "& .MuiAutocomplete-input": {
                      whiteSpace: "wrap",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        width: "auto",
                        maxWidth: "100%",
                      },
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required variant="filled">
                  <InputLabel>Urgency Level</InputLabel>
                  <Select
                    labelId="urgency-level"
                    value={urgency}
                    onChange={(e) => setUrgency(e.target.value)}
                    label="Urgency Level"
                  >
                    {URGENCY_LEVELS.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        <Chip
                          label={level.label}
                          color={level.color}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required variant="filled">
                  <InputLabel>Server Type</InputLabel>
                  <Select
                    labelId="server-type"
                    value={serverType}
                    onChange={(e) => setServerType(e.target.value)}
                    label="Server Type"
                  >
                    {SERVER_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  variant="filled"
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief summary of the requested changes..."
                  helperText="Provide a clear, concise title for your request"
                />
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <TextField
                  labelId="description-of-changes"
                  fullWidth
                  multiline
                  rows={6}
                  label="Description of Changes"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please describe in detail what changes you would like to see on this page..."
                  required
                  helperText="Be as specific as possible about what needs to be changed"
                />
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateTicketConfirmDialog}
                  disabled={
                    !selectedPage || !description.trim() || submittingTicket
                  }
                  size="large"
                >
                  {submittingTicket
                    ? "Submitting..."
                    : "Submit Modification Request"}
                </Button>
              </Grid2>
            </Grid2>
          </Box>
        </TabPanel>

        {/* Request New Feature Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <AddIcon color="primary" />
              Request New Feature
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Submit a request for a completely new feature or functionality
            </Typography>

            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required variant="filled">
                  <InputLabel>Urgency Level</InputLabel>
                  <Select
                    value={urgency}
                    onChange={(e) => setUrgency(e.target.value)}
                    label="Urgency Level"
                  >
                    {URGENCY_LEVELS.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        <Chip
                          label={level.label}
                          color={level.color}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required variant="filled">
                  <InputLabel>Server Type</InputLabel>
                  <Select
                    value={serverType}
                    onChange={(e) => setServerType(e.target.value)}
                    label="Server Type"
                  >
                    {SERVER_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required
                  variant="filled"
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief summary of the new feature..."
                  helperText="Provide a clear, concise title for your feature request"
                  sx={{ mb: 2 }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Feature Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please describe the new feature you would like to see implemented..."
                  required
                  helperText="Be as detailed as possible about the new functionality you want"
                />
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateTicketConfirmDialog}
                  disabled={!description.trim() || submittingTicket}
                  size="large"
                >
                  {submittingTicket
                    ? "Submitting..."
                    : "Submit Feature Request"}
                </Button>
              </Grid2>
            </Grid2>
          </Box>
        </TabPanel>

        {/* Support Tickets Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <BugReportIcon color="primary" />
              Support Tickets
            </Typography>
          </Box>

          <TicketFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
            ticketType="support"
          />

          {loadingTickets ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : supportTickets.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                color: "text.secondary",
              }}
            >
              <BugReportIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                No Support Tickets
              </Typography>
              <Typography variant="body2" textAlign="center">
                You haven't created any support tickets yet
              </Typography>
            </Box>
          ) : (
            <Grid2 container spacing={2}>
              {supportTickets.map((ticket) => (
                <Grid2 size={{ xs: 12 }} key={ticket._id}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {ticket.type === "modification"
                              ? "Modification"
                              : "New Feature Request"}
                          </Typography>
                          {ticket.type === "modification" && ticket.page && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              Category:{" "}
                              {AVAILABLE_PAGES.find(
                                (p) => p.path === ticket.page
                              )?.name || ticket.page}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Chip
                            label={`Urgency: ${URGENCY_LEVELS.find((u) => u.value === ticket.urgency)?.label}`}
                            color={getUrgencyColor(ticket.urgency)}
                            size="small"
                          />
                          <Chip
                            label={ticket.serverType}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label={ticket.status}
                            color={getStatusColor(ticket.status)}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {ticket.title && (
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1,
                            fontWeight: "bold",
                            color: "primary.main",
                          }}
                        >
                          {ticket.title}
                        </Typography>
                      )}

                      <Typography
                        variant="body1"
                        sx={{ mb: 2, whiteSpace: "pre-wrap" }}
                      >
                        {ticket.description}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Created: {formatDate(ticket.created_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          By: {ticket.user_email}
                        </Typography>
                      </Box>
                    </CardContent>
                    <CardActions>
                      {ticket.status === "pending" && (
                        <>
                          {DEV_USER?.includes(currentUser?.user?.email) && (
                            <>
                              <Button
                                size="small"
                                startIcon={<CheckCircleIcon />}
                                onClick={() =>
                                  openConfirmDialog(
                                    "Accept Support Ticket",
                                    "Are you sure you want to accept this support ticket?",
                                    "accepted",
                                    ticket._id
                                  )
                                }
                                color="success"
                              >
                                Accept
                              </Button>
                              <Button
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={() =>
                                  openConfirmDialog(
                                    "Reject Support Ticket",
                                    "Are you sure you want to reject this support ticket?",
                                    "rejected",
                                    ticket._id
                                  )
                                }
                                color="error"
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </>
                      )}

                      {ticket.status === "accepted" &&
                        DEV_USER?.includes(currentUser?.user?.email) && (
                          <Button
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() =>
                              openConfirmDialog(
                                "Mark Ticket as Completed",
                                "Are you sure you want to mark this support ticket as completed?",
                                "completed",
                                ticket._id
                              )
                            }
                            color="success"
                          >
                            Mark Completed
                          </Button>
                        )}

                      {ticket.status === "pending" &&
                        !DEV_USER?.includes(currentUser?.user?.email) && (
                          <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() =>
                              openConfirmDialog(
                                "Delete Support Ticket",
                                "Are you sure you want to delete this support ticket? This action cannot be undone.",
                                "delete",
                                ticket._id
                              )
                            }
                            color="error"
                            sx={{ ml: "auto" }}
                          >
                            Delete
                          </Button>
                        )}
                    </CardActions>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          )}

          {/* Pagination for Support Tickets */}
          {supportTickets.length > 0 &&
            pagination.supportTickets.totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={pagination.supportTickets.totalPages}
                  page={pagination.supportTickets.page}
                  onChange={(event, page) =>
                    handlePageChange(event, page, "support")
                  }
                  color="primary"
                  size="large"
                />
              </Box>
            )}
        </TabPanel>

        {/* Completed Tickets Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CheckCircleIcon color="primary" />
              Completed Tickets
            </Typography>
          </Box>

          <TicketFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
            ticketType="completed"
          />

          {completedTicketError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setCompletedTicketError("")}
            >
              {completedTicketError}
            </Alert>
          )}

          {loadingCompletedTickets ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : completedTickets.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                color: "text.secondary",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                No Completed Tickets
              </Typography>
              <Typography variant="body2" textAlign="center">
                No completed tickets found matching your criteria
              </Typography>
            </Box>
          ) : (
            <Grid2 container spacing={2}>
              {completedTickets.map((ticket) => (
                <Grid2 size={{ xs: 12 }} key={ticket._id}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {ticket.type === "modification"
                              ? "Page Modification"
                              : "New Feature Request"}
                          </Typography>
                          {ticket.type === "modification" && ticket.page && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              Page:{" "}
                              {AVAILABLE_PAGES.find(
                                (p) => p.path === ticket.page
                              )?.name || ticket.page}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Chip
                            label={`Urgency: ${URGENCY_LEVELS.find((u) => u.value === ticket.urgency)?.label}`}
                            color={getUrgencyColor(ticket.urgency)}
                            size="small"
                          />
                          <Chip
                            label={ticket.serverType}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label={ticket.status}
                            color={getStatusColor(ticket.status)}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {ticket.title && (
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1,
                            fontWeight: "bold",
                            color: "primary.main",
                          }}
                        >
                          {ticket.title}
                        </Typography>
                      )}

                      <Typography
                        variant="body1"
                        sx={{ mb: 2, whiteSpace: "pre-wrap" }}
                      >
                        {ticket.description}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Created: {formatDate(ticket.created_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Completed:{" "}
                          {ticket.completed_at
                            ? formatDate(ticket.completed_at)
                            : "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          By: {ticket.user_email}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          )}

          {/* Pagination for Completed Tickets */}
          {completedTickets.length > 0 &&
            pagination.completedTickets.totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={pagination.completedTickets.totalPages}
                  page={pagination.completedTickets.page}
                  onChange={(event, page) =>
                    handlePageChange(event, page, "completed")
                  }
                  color="primary"
                  size="large"
                />
              </Box>
            )}
        </TabPanel>

        {/* Rejected Tickets Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CancelIcon color="error" />
              Rejected Tickets
            </Typography>
          </Box>

          <TicketFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
            ticketType="rejected"
          />

          {rejectedTicketError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setRejectedTicketError("")}
            >
              {rejectedTicketError}
            </Alert>
          )}

          {loadingRejectedTickets ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : rejectedTickets.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                color: "text.secondary",
              }}
            >
              <CancelIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                No Rejected Tickets
              </Typography>
              <Typography variant="body2" textAlign="center">
                No rejected tickets found matching your criteria
              </Typography>
            </Box>
          ) : (
            <Grid2 container spacing={2}>
              {rejectedTickets.map((ticket) => (
                <Grid2 size={{ xs: 12 }} key={ticket._id}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {ticket.type === "modification"
                              ? "Page Modification"
                              : "New Feature Request"}
                          </Typography>
                          {ticket.type === "modification" && ticket.page && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              Page:{" "}
                              {AVAILABLE_PAGES.find(
                                (p) => p.path === ticket.page
                              )?.name || ticket.page}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Chip
                            label={`Urgency: ${URGENCY_LEVELS.find((u) => u.value === ticket.urgency)?.label}`}
                            color={getUrgencyColor(ticket.urgency)}
                            size="small"
                          />
                          <Chip
                            label={ticket.serverType}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label={ticket.status}
                            color={getStatusColor(ticket.status)}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {ticket.title && (
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1,
                            fontWeight: "bold",
                            color: "primary.main",
                          }}
                        >
                          {ticket.title}
                        </Typography>
                      )}

                      <Typography
                        variant="body1"
                        sx={{ mb: 2, whiteSpace: "pre-wrap" }}
                      >
                        {ticket.description}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Created: {formatDate(ticket.created_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Rejected:{" "}
                          {ticket.updated_at
                            ? formatDate(ticket.updated_at)
                            : "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          By: {ticket.user_email}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          )}

          {/* Pagination for Rejected Tickets */}
          {rejectedTickets.length > 0 &&
            pagination.rejectedTickets.totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={pagination.rejectedTickets.totalPages}
                  page={pagination.rejectedTickets.page}
                  onChange={(event, page) =>
                    handlePageChange(event, page, "rejected")
                  }
                  color="primary"
                  size="large"
                />
              </Box>
            )}
        </TabPanel>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.action === "delete" ? "error" : "primary"}
            variant="contained"
          >
            {confirmDialog.action === "delete" ? "Delete" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reports;
